// src/pages/help/Suporte.jsx
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Headphones, Mail, MessageCircle, Clock, Shield, Zap,
  ChevronDown, Star, Send, Copy, Check, Award, Sparkles,
  ArrowRight, AlertCircle, Wifi, Server, Database, Globe
} from "lucide-react";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { useAuth } from "../../contexts/useAuth";

const BRAND = {
  primary: "#1a56db",
  primaryLight: "#3b82f6",
  primaryDark: "#1e3a5f",
  gold: "#c8a84e",
  goldLight: "#e2c97e",
  goldDark: "#8b6914",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
};

const SUPORTE = {
  email: "suporte@ilhaleads.com.br",
  assuntoPadrao: "Solicitação de Suporte - Ilha Leads",
  horario: {
    dias: "Segunda a Sexta",
    horas: "09h às 18h",
    fuso: "Horário de Brasília",
  },
  tempoResposta: "Até 4 horas em horário comercial",
};

const FAQ = [
  {
    pergunta: "Como redefinir minha senha?",
    resposta: "Clique em 'Esqueci minha senha' na tela de login. Você receberá um email com instruções para criar uma nova senha em até 5 minutos.",
    categoria: "Conta"
  },
  {
    pergunta: "Como adicionar fotos aos imóveis?",
    resposta: "Na página de cadastro ou edição do imóvel, arraste as imagens para a área de upload ou clique para selecionar. O sistema aceita JPG, PNG e WebP. A primeira foto será a capa do anúncio.",
    categoria: "Imóveis"
  },
  {
    pergunta: "O sistema funciona sem internet?",
    resposta: "Sim! O Ilha Leads é offline-first. Você pode cadastrar imóveis, clientes e transações sem conexão. Quando reconectar, tudo será sincronizado automaticamente com a nuvem.",
    categoria: "Técnico"
  },
  {
    pergunta: "Como compartilhar um imóvel no WhatsApp?",
    resposta: "No card do imóvel, clique no ícone do WhatsApp. O sistema gera uma mensagem pronta com título, preço, localização e link para a vitrine pública.",
    categoria: "Imóveis"
  },
  {
    pergunta: "Meus dados estão seguros?",
    resposta: "Sim. Usamos autenticação Firebase do Google, criptografia HTTPS, e isolamento multi-tenant. Cada imobiliária acessa apenas seus próprios dados. Estamos em conformidade com a LGPD.",
    categoria: "Segurança"
  },
  {
    pergunta: "Como exportar relatórios?",
    resposta: "Vá até Financeiro ou Relatórios no menu lateral, escolha o período e clique em 'Exportar PDF' ou 'Exportar Excel'. O arquivo será baixado automaticamente.",
    categoria: "Relatórios"
  },
  {
    pergunta: "Posso personalizar as cores e logo?",
    resposta: "Sim! O sistema é white-label. Vá em Configurações > Empresa e faça upload do seu logo, escolha as cores e configure o nome da sua imobiliária.",
    categoria: "Personalização"
  },
  {
    pergunta: "Como instalar o app no celular?",
    resposta: "Acesse app.ilhaleads.com.br pelo navegador do celular, abra o menu (⋮) e toque em 'Adicionar à tela inicial'. O app será instalado como nativo, com ícone e tudo!",
    categoria: "Técnico"
  },
];

const STATUS_SISTEMA = [
  { nome: "Aplicação Web", status: "online", icon: Globe },
  { nome: "Firestore (Banco de Dados)", status: "online", icon: Database },
  { nome: "Cloudinary (Imagens)", status: "online", icon: Server },
  { nome: "Autenticação Firebase", status: "online", icon: Shield },
  { nome: "API WhatsApp", status: "online", icon: MessageCircle },
];

export default function Suporte() {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  const [formulario, setFormulario] = useState({
    nome: user?.name || "",
    email: user?.email || "",
    assunto: "",
    mensagem: "",
    prioridade: "normal",
  });
  const [faqAberto, setFaqAberto] = useState(null);
  const [protocolo, setProtocolo] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [categoriaFaq, setCategoriaFaq] = useState("Todas");

  const categorias = useMemo(() => {
    const cats = new Set(FAQ.map(f => f.categoria));
    return ["Todas", ...Array.from(cats)];
  }, []);

  const faqFiltrado = useMemo(() => {
    if (categoriaFaq === "Todas") return FAQ;
    return FAQ.filter(f => f.categoria === categoriaFaq);
  }, [categoriaFaq]);

  const gerarProtocolo = () => {
    const now = new Date();
    const protocolo = `IL-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setProtocolo(protocolo);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    gerarProtocolo();
  };

  const copiarEmail = () => {
    navigator.clipboard?.writeText(SUPORTE.email);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const abrirEmail = () => {
    const assunto = encodeURIComponent(SUPORTE.assuntoPadrao);
    const corpo = encodeURIComponent(`\n\n---\nEnviado por: ${user?.name || 'Usuário'}\nEmail: ${user?.email || ''}\n---`);
    window.location.href = `mailto:${SUPORTE.email}?subject=${assunto}&body=${corpo}`;
  };

  const horarioComercial = () => {
    const agora = new Date();
    const dia = agora.getDay();
    const hora = agora.getHours();
    return dia >= 1 && dia <= 5 && hora >= 9 && hora < 18;
  };

  const online = horarioComercial();

  return (
    <div className="min-h-screen bg-page" style={{ backgroundColor: "#f8fafc" }}>
      <header 
        className="sticky top-0 z-50 backdrop-blur-xl border-b shadow-sm"
        style={{ 
          background: `linear-gradient(135deg, ${BRAND.primaryDark} 0%, ${BRAND.primary} 100%)`,
          borderColor: `${BRAND.gold}30`
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})` }}>
              <Headphones size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg">Central de Suporte</h1>
              <p className="text-xs text-white/70">Estamos aqui para ajudar</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{ 
              background: online ? `${BRAND.success}20` : `${BRAND.warning}20`,
              color: online ? BRAND.success : BRAND.warning,
              border: `1px solid ${online ? BRAND.success : BRAND.warning}40`
            }}>
            <div className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: online ? BRAND.success : BRAND.warning }} />
            {online ? "Equipe Online" : "Equipe Offline"}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-6'}`}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl p-6 shadow-lg border relative overflow-hidden"
            style={{ borderColor: `${BRAND.primary}20` }}
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-5"
              style={{ background: `radial-gradient(circle, ${BRAND.primary}, transparent)` }} />
            <div className="flex items-start gap-4 relative z-10">
              <div className="p-3 rounded-xl flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${BRAND.primary}15, ${BRAND.primaryLight}20)` }}>
                <Mail size={24} style={{ color: BRAND.primary }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg" style={{ color: BRAND.primaryDark }}>Email Profissional</h3>
                <p className="text-sm mt-1" style={{ color: "#64748b" }}>
                  Envie sua dúvida e receba resposta em {SUPORTE.tempoResposta}.
                </p>
                <div className="mt-3 flex items-center gap-2 p-2 rounded-xl border"
                  style={{ borderColor: `${BRAND.primary}20`, backgroundColor: `${BRAND.primary}05` }}>
                  <code className="flex-1 text-sm font-medium truncate" style={{ color: BRAND.primary }}>
                    {SUPORTE.email}
                  </code>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={copiarEmail}
                    className="p-1.5 rounded-lg hover:bg-card transition-colors flex-shrink-0"
                    title="Copiar email"
                  >
                    {copiado ? <Check size={16} style={{ color: BRAND.success }} /> : <Copy size={16} style={{ color: BRAND.primary }} />}
                  </motion.button>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={abrirEmail}
                  className="mt-3 w-full py-2.5 rounded-xl font-medium text-sm text-white flex items-center justify-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})` }}
                >
                  <Send size={16} />
                  Enviar Email Agora
                </motion.button>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl p-6 shadow-lg border relative overflow-hidden"
            style={{ borderColor: `${BRAND.gold}20` }}
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-5"
              style={{ background: `radial-gradient(circle, ${BRAND.gold}, transparent)` }} />
            <div className="flex items-start gap-4 relative z-10">
              <div className="p-3 rounded-xl flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${BRAND.gold}15, ${BRAND.goldLight}20)` }}>
                <Clock size={24} style={{ color: BRAND.goldDark }} />
              </div>
              <div>
                <h3 className="font-bold text-lg" style={{ color: BRAND.primaryDark }}>Horário de Atendimento</h3>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium" style={{ color: BRAND.primaryDark }}>📅 Dias:</span>
                    <span style={{ color: "#64748b" }}>{SUPORTE.horario.dias}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium" style={{ color: BRAND.primaryDark }}>🕐 Horário:</span>
                    <span style={{ color: "#64748b" }}>{SUPORTE.horario.horas}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium" style={{ color: BRAND.primaryDark }}>🌎 Fuso:</span>
                    <span style={{ color: "#64748b" }}>{SUPORTE.horario.fuso}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full"
                  style={{ 
                    background: online ? `${BRAND.success}15` : `${BRAND.warning}15`,
                    color: online ? BRAND.success : BRAND.warning,
                  }}>
                  <div className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: online ? BRAND.success : BRAND.warning }} />
                  {online ? "Atendimento disponível agora" : "Responderemos no próximo dia útil"}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {!protocolo ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-6 shadow-lg border"
            style={{ borderColor: `${BRAND.gold}20` }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={20} style={{ color: BRAND.gold }} />
              <h2 className="text-lg font-bold" style={{ color: BRAND.primaryDark }}>
                Enviar Solicitação de Suporte
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-4'}`}>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: BRAND.primaryDark }}>Nome</label>
                  <input
                    type="text"
                    value={formulario.nome}
                    onChange={(e) => setFormulario({ ...formulario, nome: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border outline-none transition-all focus:ring-2 text-sm"
                    style={{ borderColor: `${BRAND.primary}20` }}
                    placeholder="Seu nome completo"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: BRAND.primaryDark }}>Email</label>
                  <input
                    type="email"
                    value={formulario.email}
                    onChange={(e) => setFormulario({ ...formulario, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border outline-none transition-all focus:ring-2 text-sm"
                    style={{ borderColor: `${BRAND.primary}20` }}
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: BRAND.primaryDark }}>Assunto</label>
                <input
                  type="text"
                  value={formulario.assunto}
                  onChange={(e) => setFormulario({ ...formulario, assunto: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border outline-none transition-all focus:ring-2 text-sm"
                  style={{ borderColor: `${BRAND.primary}20` }}
                  placeholder="Ex: Dúvida sobre cadastro de imóvel"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: BRAND.primaryDark }}>Mensagem</label>
                <textarea
                  value={formulario.mensagem}
                  onChange={(e) => setFormulario({ ...formulario, mensagem: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border outline-none transition-all focus:ring-2 text-sm resize-none"
                  style={{ borderColor: `${BRAND.primary}20` }}
                  rows={4}
                  placeholder="Descreva sua dúvida ou problema em detalhes..."
                  required
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium" style={{ color: BRAND.primaryDark }}>Prioridade:</label>
                {["normal", "urgente"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setFormulario({ ...formulario, prioridade: p })}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all capitalize ${
                      formulario.prioridade === p ? 'text-white shadow' : 'border'
                    }`}
                    style={formulario.prioridade === p ? {
                      background: p === 'urgente' ? `linear-gradient(135deg, ${BRAND.danger}, #dc2626)` : `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`
                    } : { borderColor: `${BRAND.primary}20`, color: "#64748b" }}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full py-3 rounded-xl font-medium text-white flex items-center justify-center gap-2 shadow-lg"
                style={{ background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})` }}
              >
                <Send size={18} />
                Enviar Solicitação
              </motion.button>
            </form>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card rounded-2xl p-8 shadow-lg border text-center"
            style={{ borderColor: `${BRAND.success}30` }}
          >
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-xl font-bold mb-2" style={{ color: BRAND.success }}>
              Solicitação Enviada com Sucesso!
            </h3>
            <p className="text-sm mb-4" style={{ color: "#64748b" }}>
              Seu protocolo de atendimento:
            </p>
            <div className="inline-block px-6 py-3 rounded-xl border-2 mb-4"
              style={{ borderColor: `${BRAND.gold}50`, background: `${BRAND.gold}05` }}>
              <code className="text-lg font-bold tracking-wider" style={{ color: BRAND.primary }}>
                {protocolo}
              </code>
            </div>
            <p className="text-sm" style={{ color: "#64748b" }}>
              Responderemos em até {SUPORTE.tempoResposta}.<br />
              Guarde este número para referência.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => { setProtocolo(null); setFormulario({ nome: user?.name || "", email: user?.email || "", assunto: "", mensagem: "", prioridade: "normal" }); }}
              className="mt-4 px-6 py-2 rounded-xl text-sm font-medium border"
              style={{ borderColor: `${BRAND.primary}30`, color: BRAND.primary }}
            >
              Nova Solicitação
            </motion.button>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-2xl p-6 shadow-lg border"
          style={{ borderColor: `${BRAND.primary}20` }}
        >
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Award size={20} style={{ color: BRAND.gold }} />
              <h2 className="text-lg font-bold" style={{ color: BRAND.primaryDark }}>
                Perguntas Frequentes (FAQ)
              </h2>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
            {categorias.map(cat => (
              <motion.button
                key={cat}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCategoriaFaq(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  categoriaFaq === cat ? 'text-white shadow' : 'border'
                }`}
                style={categoriaFaq === cat ? {
                  background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`
                } : { borderColor: `${BRAND.primary}20`, color: "#64748b" }}
              >
                {cat}
              </motion.button>
            ))}
          </div>
          <div className="space-y-2">
            {faqFiltrado.map((item, i) => (
              <div key={i} className="border rounded-xl overflow-hidden"
                style={{ borderColor: `${BRAND.primary}15` }}>
                <button
                  onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                  className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-muted transition-colors"
                >
                  <span className="font-medium text-sm pr-4" style={{ color: BRAND.primaryDark }}>
                    {item.pergunta}
                  </span>
                  <motion.div
                    animate={{ rotate: faqAberto === i ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown size={16} style={{ color: BRAND.primary }} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {faqAberto === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-1">
                        <p className="text-sm leading-relaxed" style={{ color: "#64748b" }}>
                          {item.resposta}
                        </p>
                        <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${BRAND.primary}10`, color: BRAND.primary }}>
                          {item.categoria}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card rounded-2xl p-6 shadow-lg border"
          style={{ borderColor: `${BRAND.success}20` }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield size={18} style={{ color: BRAND.success }} />
            <h3 className="font-bold text-sm" style={{ color: BRAND.primaryDark }}>
              Status do Sistema
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: `${BRAND.success}15`, color: BRAND.success }}>
              Todos operacionais
            </span>
          </div>
          <div className={`grid ${isMobile ? 'grid-cols-2 gap-2' : 'grid-cols-5 gap-3'}`}>
            {STATUS_SISTEMA.map((servico, i) => {
              const Icon = servico.icon;
              return (
                <div key={i} className="text-center p-3 rounded-xl"
                  style={{ backgroundColor: `${BRAND.success}05` }}>
                  <Icon size={18} style={{ color: BRAND.success }} className="mx-auto mb-1" />
                  <p className="text-xs font-medium" style={{ color: BRAND.primaryDark }}>{servico.nome}</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: BRAND.success }} />
                    <span className="text-[10px] capitalize" style={{ color: BRAND.success }}>{servico.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}